import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Text, StyleSheet, Pressable, View, StyleProp, ViewStyle, ActivityIndicator } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useInfoModal } from '@/contexts/infoModalContext';
import { useAuth } from '@/contexts/authContext';
import { router } from 'expo-router';
import { i18n } from '@/utils/i18n';

interface AISearchButtonProps {
  onPress: () => void;
  label?: string;
  subLabel?: string;
  containerStyle?: StyleProp<ViewStyle>;
  gradientStyle?: StyleProp<ViewStyle>;
  isLoading?: boolean;
  showNoResults?: boolean;
}

export const AISearchButton: React.FC<AISearchButtonProps> = ({
  onPress,
  label = i18n.t('mix.ai_search.default_label'),
  subLabel = i18n.t('mix.ai_search.default_sub_label'),
  containerStyle,
  gradientStyle,
  isLoading = false,
  showNoResults = false
}) => {
  const { state } = useAuth();
  const { showInfo } = useInfoModal();

  // Locales
  const registerTitle = i18n.t('register_modal.title');
  const registerSubtitle = i18n.t('register_modal.ai_search_subtitle');
  const registerButtonLabel = i18n.t('register_modal.button_label');

  const LOADING_PHRASES = useMemo(
    () => [
      i18n.t('mix.ai_search.status_thinking'),
      i18n.t('mix.ai_search.status_analyzing'),
      i18n.t('mix.ai_search.status_fetching')
    ],
    []
  );

  const [currentLoadingText, setCurrentLoadingText] = useState(LOADING_PHRASES[0]);

  useEffect(() => {
    if (isLoading) {
      let phraseIndex = 0;
      const intervalId = setInterval(() => {
        phraseIndex = (phraseIndex + 1) % LOADING_PHRASES.length;
        setCurrentLoadingText(LOADING_PHRASES[phraseIndex]);
      }, 1500);

      return () => clearInterval(intervalId);
    }
    setCurrentLoadingText(LOADING_PHRASES[0]);
  }, [isLoading, LOADING_PHRASES]);

  const onButtonPress = useCallback(() => {
    if (isLoading) return;

    if (state.user?.user.role !== 'INDIVIDUAL' && state.user?.user.role !== 'NON_PROFIT_ORG') {
      showInfo({
        type: 'full',
        title: registerTitle,
        subtitle: registerSubtitle,
        buttonLabel: registerButtonLabel,
        onButtonPress: () => {
          router.navigate('/');
        }
      });
    } else {
      onPress();
    }
  }, [isLoading, state.user?.user.role, showInfo, registerTitle, registerSubtitle, registerButtonLabel, onPress]);

  return (
    <Pressable
      onPress={onButtonPress}
      disabled={isLoading}
      style={({ pressed }) => [
        containerStyle,
        pressed && !isLoading && styles.pressed,
        isLoading && styles.loadingDisabled
      ]}>
      <LinearGradient
        colors={['#1a1a1a', '#B38B42', '#F5C542']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0.8 }}
        style={[styles.gradient, gradientStyle]}>
        {isLoading ? (
          <>
            <View style={styles.iconContainer}>
              <ActivityIndicator color="#F5C542" size="small" />
            </View>
            <View style={styles.textContainer}>
              <Text style={[styles.label, { color: '#F5C542' }]}>{currentLoadingText}</Text>
            </View>
          </>
        ) : (
          <>
            <View style={styles.iconContainer}>
              <MaterialCommunityIcons name="book-cross" size={24} color="#fff" />
            </View>

            <View style={styles.textContainer}>
              {showNoResults ? (
                <View style={styles.noResultsContainer}>
                  <MaterialCommunityIcons name="alert-circle-outline" size={14} color="#FF6B6B" />
                  <Text style={styles.noResultsText}>{i18n.t('mix.ai_search.no_results')}</Text>
                </View>
              ) : (
                subLabel && <Text style={styles.subLabel}>{subLabel}</Text>
              )}
              <View style={styles.labelRow}>
                <Text style={styles.label}>{showNoResults ? i18n.t('mix.ai_search.refine_search') : label}</Text>
                <MaterialCommunityIcons name="arrow-right" size={16} color="#F5F5DC" style={styles.arrow} />
              </View>
            </View>
          </>
        )}
      </LinearGradient>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  loadingDisabled: {
    opacity: 0.9
  },

  gradient: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(245, 197, 66, 0.3)',
    position: 'relative',
    overflow: 'hidden'
  },

  pressed: {
    opacity: 0.85
  },

  iconContainer: {
    backgroundColor: 'rgba(0,0,0,0.2)',
    padding: 8,
    borderRadius: 12,
    marginRight: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)'
  },

  textContainer: {
    flex: 1,
    justifyContent: 'center'
  },

  subLabel: {
    color: '#F5F5DC',
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 2,
    letterSpacing: 0.5
  },

  noResultsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 12,
    alignSelf: 'flex-start'
  },

  noResultsText: {
    color: '#FF6B6B',
    fontSize: 12,
    fontWeight: '700',
    marginLeft: 6,
    letterSpacing: 0.5
  },

  labelRow: {
    flexDirection: 'row',
    alignItems: 'center'
  },

  label: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.3
  },

  arrow: {
    marginLeft: 6,
    opacity: 0.8
  }
});

export default React.memo(AISearchButton);
